// Fuso orario con cui l'app mostra e legge gli orari. Il database e le API parlano in UTC; qui si decide in che fuso li vede
// l'utente: quello che ha scelto nel profilo, altrimenti quello dell'azienda (user.company_timezone). Prima del login,
// nelle pagine pubbliche (per esempio la conferma di un appuntamento), vale il fuso del dispositivo.
//
// Non si usano getHours()/getDate() & co. su orari del database: seguono il fuso del dispositivo, non quello scelto.
// Si usano queste funzioni, che lavorano con Intl e il fuso indicato.

export const DEFAULT_TIMEZONE = 'Europe/Rome';

export const isValidTimezone = (zone) => {
  if (!zone || typeof zone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

export const deviceTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

let activeZone = null;

/** Fuso che l'utente deve vedere: il suo, altrimenti quello dell'azienda. */
export const userTimezone = (user) => {
  if (isValidTimezone(user?.timezone)) return user.timezone;
  if (isValidTimezone(user?.company_timezone)) return user.company_timezone;
  return DEFAULT_TIMEZONE;
};

/** Imposta il fuso in uso (da chiamare quando cambia l'utente). Senza argomento torna a quello del dispositivo. */
export const setActiveTimezone = (zone) => {
  activeZone = isValidTimezone(zone) ? zone : null;
};

export const activeTimezone = () => activeZone || deviceTimezone();

const partsFormatters = {};
const formatterFor = (zone) => {
  if (!partsFormatters[zone]) {
    partsFormatters[zone] = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  return partsFormatters[zone];
};

/** Anno, mese, giorno, ore, minuti, secondi che un istante ha nel fuso indicato. */
export const zonedParts = (value, zone = activeTimezone()) => {
  const date = value instanceof Date ? value : new Date(value);
  const out = {};
  formatterFor(zone).formatToParts(date).forEach((p) => {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  });
  if (out.hour === 24) out.hour = 0;
  return out;
};

const pad = (n) => String(n).padStart(2, '0');

/** Differenza, in millisecondi, tra l'ora scritta nel fuso e UTC nell'istante dato. */
const offsetMs = (date, zone) => {
  const p = zonedParts(date, zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(date.getTime() / 1000) * 1000;
};

/** Istante -> "YYYY-MM-DDTHH:mm" nel fuso (formato dei campi datetime-local). */
export const toZonedInput = (value, zone = activeTimezone()) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, zone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};

/** "YYYY-MM-DDTHH:mm" scritto nel fuso -> Date (l'istante vero). Gestisce ora legale e solare. */
export const fromZonedInput = (text, zone = activeTimezone()) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(text || ''));
  if (!m) return new Date(NaN);
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0));
  let result = guess - offsetMs(new Date(guess), zone);
  const second = guess - offsetMs(new Date(result), zone);
  if (second !== result) result = second;
  return new Date(result);
};

/** Giorno ("YYYY-MM-DD") che un istante ha nel fuso. */
export const zonedDayKey = (value, zone = activeTimezone()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const p = zonedParts(date, zone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

/** Giorno "YYYY-MM-DD" + n giorni (aritmetica di calendario: non dipende da fusi né ora legale). */
export const addDaysToKey = (dayKey, days) => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

/** Date locale di un giorno "YYYY-MM-DD", per le griglie di calendario (si usano solo giorno, mese e anno). */
export const dateFromDayKey = (dayKey) => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Mezzanotte di un giorno ("YYYY-MM-DD") nel fuso, come istante. */
export const startOfZonedDay = (dayKey, zone = activeTimezone()) => fromZonedInput(`${dayKey}T00:00`, zone);

/** Ora di un istante nel fuso, nel formato della lingua ("10:00", "10:00 AM"). */
export const zonedTime = (value, locale, zone = activeTimezone()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: zone });
};
